// src/lib/error-handler.ts
import { useToast } from "@/hooks/use-toast";
import { AxiosError } from "axios";

interface ApiError {
  detail: string;
  request_id: string;
  error_type?: string;
}

interface ErrorResponse {
  detail: string;
  request_id: string;
  error_type?: string;
}

// Map of backend error types to user-friendly messages (used as fallbacks)
const errorMessages: Record<string, string> = {
  "QuoteNotFoundError": "The quote you're looking for doesn't exist.",
  "QuoteNotActiveError": "This quote is no longer active.",
  "QuoteValidationError": "Invalid quote data. Please check your input.",
  "QuoteRefreshError": "Failed to refresh quote prices.",
  "QuoteAcceptanceError": "Failed to accept the quote.",
  "DatabaseError": "A database error occurred. Please try again.",
  "PricingEngineError": "Failed to calculate prices. Please try again.",
  "ClientNotFoundError": "The client you're looking for doesn't exist.",
  "ClientValidationError": "Invalid client data. Please check your input.",
  "ClientUpdateError": "Failed to update client information.",
  "ClientDeletionError": "Failed to delete client.",
  "AuthenticationError": "Invalid email or password. Please check your credentials.",
  "UserNotFoundError": "The user you're looking for doesn't exist.",
  "UserValidationError": "Invalid user data. Please check your input.",
  "UserCreationError": "Failed to create user.",
  "UserUpdateError": "Failed to update user information.",
  "UserDeletionError": "Failed to delete user.",
  "TradeNotFoundError": "The trade you're looking for doesn't exist.",
  "TradeValidationError": "Invalid trade data. Please check your input.",
  "TradeExecutionError": "Failed to execute trade.",
  "AssetNotFoundError": "The asset you're looking for doesn't exist.",
  "AssetValidationError": "Invalid asset data. Please check your input.",
  "AssetUpdateError": "Failed to update asset information.",
  "AssetDeletionError": "Failed to delete asset.",
  "VirtualMarketNotFoundError": "The virtual market you're looking for doesn't exist.",
  "VirtualMarketValidationError": "Invalid virtual market data. Please check your input.",
  "VirtualMarketCreationError": "Failed to create virtual market.",
  "VirtualMarketUpdateError": "Failed to update virtual market information.",
  "VirtualMarketDeletionError": "Failed to delete virtual market.",
  "RouteError": "Failed to calculate trading route.",
  "RouteValidationError": "Invalid route data. Please check your input.",
  "RouteCalculationError": "Failed to calculate route.",
  "RoutePriceError": "Failed to calculate route prices.",
  "ExchangeError": "Failed to communicate with exchange.",
  "TradeableAssetNotActiveError": "This trading pair is not currently active.",
};

// Get a user-friendly message for a given error type (used as fallback)
export const getUserFriendlyMessage = (errorType: string): string => {
  return errorMessages[errorType] || "An unexpected error occurred. Please try again.";
};

// Log the error to console (for development)
export const logError = (context: string, error: unknown): void => {
  if (import.meta.env.DEV) {
    const apiError = extractApiError(error);
    if (apiError) {
      console.error(`API Error (${context}):`, {
        detail: apiError.detail,
        request_id: apiError.request_id,
        error_type: apiError.error_type,
      });
    } else {
      console.error(`Error (${context}):`, error);
    }
  }
};

// Extract API error from Axios error response
export const extractApiError = (error: unknown): ApiError | null => {
  if (!error || typeof error !== 'object') return null;

  const axiosError = error as AxiosError<ErrorResponse>;

  // Check for our standard API error format
  if (axiosError?.response?.data?.detail) {
    return {
      detail: axiosError.response.data.detail,
      request_id: axiosError.response.data.request_id || "",
      error_type: axiosError.response.data.error_type,
    };
  }

  // Special case for 401 errors
  if (axiosError?.response?.status === 401) {
    return {
      detail: "Invalid email or password",
      request_id: axiosError.response?.data?.request_id || "",
      error_type: "AuthenticationError",
    };
  }

  // Special case for 403 errors
  if (axiosError?.response?.status === 403) {
    return {
      detail: "You don't have permission to perform this action",
      request_id: axiosError.response?.data?.request_id || "",
      error_type: "AuthorizationError",
    };
  }

  // Special case for 404 errors
  if (axiosError?.response?.status === 404) {
    return {
      detail: "The requested resource was not found",
      request_id: axiosError.response?.data?.request_id || "",
      error_type: "NotFoundError",
    };
  }

  return null;
};

// Utility function to handle API errors
export const handleApiError = (
  context: string,
  error: unknown,
  toast: ReturnType<typeof useToast>["toast"]
) => {
  logError(context, error);

  const apiError = extractApiError(error);

  if (apiError) {
    // Always use the backend's error message if available
    const userMessage = apiError.detail;

    // Show error toast with the backend's message
    toast({
      title: "Error",
      description: userMessage,
      variant: "error",
    });
  } else {
    // Check for network errors
    const axiosError = error as AxiosError;
    if (axiosError.message === "Network Error") {
      toast({
        title: "Connection Error",
        description: "Unable to connect to the server. Please check your internet connection.",
        variant: "error",
      });
      return;
    }

    // Fallback for unknown errors
    toast({
      title: "Error",
      description: "An unexpected error occurred. Please try again.",
      variant: "error",
    });
  }
};

// Success notification helper
export const showSuccessToast = (
  toast: ReturnType<typeof useToast>["toast"],
  title: string,
  description: string
) => {
  toast({
    title,
    description,
    variant: "success",
  });
};
